<script lang="ts">
  import { tick, untrack } from 'svelte';
  import { navigate } from '../lib/router.svelte';
  import AdminShell from './AdminShell.svelte';
  import PageContainer from '../lib/ui/PageContainer.svelte';
  import PageHeader from '../lib/ui/PageHeader.svelte';
  import Avatar from '@ui/Avatar.svelte';
  import StateBlock from '@ui/StateBlock.svelte';
  import SkoolContent from '@ui/SkoolContent.svelte';
  import MemberHover from '@ui/MemberHover.svelte';
  import {
    redirectIfAwaitingOnboarding,
    getToken,
    handle401,
    parseTs,
    type AdminUser,
  } from '../lib/admin';
  import { community, withCommunity, onCommunityChange } from '../lib/community.svelte';
  import { asAdminUser, cachedBootstrap, ensureBootstrap } from '../lib/session';
  import { fetchAskers, fetchConversations, type AskerItem, type ConversationItem, type MessageItem } from '@api';

  /**
   * Conversations — doc 04 §5.5.
   *
   * WHAT THIS PAGE USED TO DO. One `GET /admin/conversations` with the server's
   * default `limit=500`, which hydrates every conversation with every message AND
   * the citation list of every message, so the browser could group, count, filter,
   * merge and gap-annotate one member's stream. It was the second-largest response
   * in the app and 499 of the 500 conversations were downloaded to render one.
   *
   * WHAT IT DOES NOW. The rail is a page of asker SUMMARIES with their counts
   * computed in SQL (`…/ai/askers`), and the transcript is one request for the one
   * member being read (`…/ai/conversations?asker=&include=messages`), returned as a
   * merged chronological stream whose 1-hour gap markers are `gap_before_hours` on
   * the message — the threshold is the server's now, not this template's.
   */

  const RAIL_PAGE = 200; // the v1 maximum; most communities fit in one page

  let me = $state<AdminUser | null>(null);
  let askers = $state<AskerItem[]>([]);
  let askersCursor = $state<string | null>(null);
  /** Every asker in the community, not just the loaded ones. */
  let askersTotal = $state<number | null>(null);
  let moreLoading = $state(false);
  let selectedKey = $state<string | null>(null);
  /** The selected member's conversations, messages included. */
  let convs = $state<ConversationItem[]>([]);
  let streamLoading = $state(false);
  let mobileDetail = $state(false);
  let loading = $state(true);
  let error = $state<string | null>(null);

  const selected = $derived(askers.find((u) => u.key === selectedKey) ?? null);

  // Deep-link seeds from the AI insights page: `?user=<external_id>` preselects an
  // asker; `?source=<page_title>` filters to members whose transcript cites that lesson.
  const _qs = new URLSearchParams(window.location.search);
  let pendingUser: string | null = _qs.get('user');
  let sourceFilter = $state<string | null>(_qs.get('source'));

  /** The lessons cited for this asker come back ON the row (top 8, most-cited
   *  first), so the source filter no longer needs every message of every
   *  conversation in the browser to answer it. */
  function citesSource(u: AskerItem, src: string): boolean {
    return u.sources.some((s) => s.page_title === src);
  }

  // Member search over the LOADED askers (name / handle), plus the optional source
  // filter. `…/ai/askers` takes no `?q=` — see the note in the report; when it does,
  // this becomes a server-side filter and the search covers the whole community
  // rather than the loaded page.
  let q = $state('');
  const filteredUsers = $derived.by(() => {
    let users = askers;
    const src = sourceFilter;
    if (src) users = users.filter((u) => citesSource(u, src));
    const needle = q.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((u) => `${displayName(u)} ${u.handle ?? ''}`.toLowerCase().includes(needle));
  });

  // The member's whole history as one chronological chat stream (oldest → newest,
  // like Telegram), merging all their threads. A quiet spell the server flagged
  // becomes a divider ("13 days · no activity") so the breaks between sessions read.
  type StreamItem =
    | { kind: 'gap'; hours: number; key: string }
    | { kind: 'msg'; m: MessageItem; key: string };
  const stream = $derived.by<StreamItem[]>(() => {
    const msgs = convs
      .flatMap((c) => c.messages ?? [])
      .slice()
      .sort((a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? '') || a.id - b.id);
    const items: StreamItem[] = [];
    for (const m of msgs) {
      if (m.gap_before_hours != null) {
        items.push({ kind: 'gap', hours: m.gap_before_hours, key: `gap-${m.id}` });
      }
      items.push({ kind: 'msg', m, key: `msg-${m.id}` });
    }
    return items;
  });
  const hasAnySources = $derived(stream.some((it) => it.kind === 'msg' && it.m.sources.length > 0));

  function gapLabel(hours: number): string {
    if (hours < 1) return `${Math.round(hours * 60)} min later`;
    if (hours < 24) return `${Math.round(hours)} hour${Math.round(hours) === 1 ? '' : 's'} later`;
    const d = hours / 24;
    if (d < 30) return `${Math.round(d)} day${Math.round(d) === 1 ? '' : 's'} · no activity`;
    return `${Math.round(d / 30)} month${Math.round(d / 30) === 1 ? '' : 's'} · no activity`;
  }

  // Sources unfolding: a global "show all" plus per-message toggles.
  let showAllSources = $state(false);
  let expandedSources = $state<Set<number>>(new Set());
  const isExpanded = (id: number): boolean => showAllSources || expandedSources.has(id);
  function toggleSources(id: number): void {
    const s = new Set(expandedSources);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    expandedSources = s;
  }

  // On switching member: reset source folds and jump to the newest message (Telegram).
  let scroller = $state<HTMLElement | null>(null);
  $effect(() => {
    selectedKey; // re-run when the member changes
    stream; // …and once their stream has arrived
    showAllSources = sourceFilter != null; // reveal citations when arriving via a source link
    expandedSources = new Set();
    void tick().then(() => {
      if (scroller) scroller.scrollTop = scroller.scrollHeight;
    });
  });

  function fmt(iso: string | null): string {
    if (!iso) return '';
    const d = parseTs(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
  }
  function displayName(u: AskerItem): string {
    return u.name || u.handle || (u.external_id ? `Member ${u.external_id.slice(0, 8)}` : 'Anonymous');
  }
  function selectUser(key: string): void {
    selectedKey = key;
    mobileDetail = true;
  }

  /** The community the rail was loaded for — claimed before the await. */
  let lastId: number | null = null;
  /** `init` has settled and owns the first rail load. On a FIRST visit (no id in
   *  localStorage) `community.loaded` flips while `withCommunity` is still deciding
   *  which id to use; without this gate the effect would duplicate that fetch. */
  let ready = $state(false);

  /** Throws on failure: `withCommunity` needs the scope error to retry against the
   *  bootstrap's resolved id. */
  async function fetchRail(id: number): Promise<void> {
    lastId = id;
    const first = await fetchAskers(id, { limit: RAIL_PAGE });
    let rows = first.items;
    askersCursor = first.next_cursor;
    askersTotal = first.total;
    // A `?user=` deep link may name someone below the first page. Walk forward for
    // them — bounded — rather than silently opening the wrong member's transcript.
    const want = pendingUser;
    if (want) {
      let guard = 4;
      while (
        askersCursor != null &&
        guard-- > 0 &&
        !rows.some((a) => a.key === want || a.external_id === want)
      ) {
        const more = await fetchAskers(id, { limit: RAIL_PAGE, cursor: askersCursor });
        rows = [...rows, ...more.items];
        askersCursor = more.next_cursor;
      }
    }
    askers = rows;
    selectedKey = pickInitial(rows);
    mobileDetail = false;
  }

  function pickInitial(rows: AskerItem[]): string | null {
    let pick = rows[0]?.key ?? null;
    const src = sourceFilter;
    if (pendingUser) {
      const want = pendingUser;
      const u = rows.find((x) => x.key === want || x.external_id === want);
      if (u) pick = u.key;
      pendingUser = null;
    } else if (src) {
      const u = rows.find((x) => citesSource(x, src));
      if (u) pick = u.key;
    }
    return pick;
  }

  async function loadMore(): Promise<void> {
    const id = community.loaded ? community.selectedId : lastId;
    const cursor = askersCursor;
    if (id == null || cursor == null || moreLoading) return;
    moreLoading = true;
    try {
      const page = await fetchAskers(id, { limit: RAIL_PAGE, cursor });
      askers = [...askers, ...page.items];
      askersCursor = page.next_cursor;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
    } finally {
      moreLoading = false;
    }
  }

  async function init(): Promise<void> {
    if (!getToken()) {
      navigate('/admin/login');
      return;
    }
    error = null;
    try {
      // The bootstrap and the rail go out in the SAME round trip (doc 04 §4.1).
      const [boot] = await Promise.all([ensureBootstrap(), withCommunity(fetchRail)]);
      me = asAdminUser(boot);
      if (redirectIfAwaitingOnboarding(me, navigate)) return;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      const boot = cachedBootstrap();
      if (boot) {
        me = asAdminUser(boot);
        if (redirectIfAwaitingOnboarding(me, navigate)) return;
      }
      error = err instanceof Error ? err.message : 'Failed to load conversations.';
    } finally {
      loading = false;
      ready = true;
    }
  }

  // Reload the rail when the shared community selection changes — and only then.
  onCommunityChange((id) => void reloadRail(id), { gate: () => ready });

  async function reloadRail(id: number): Promise<void> {
    loading = true;
    error = null;
    try {
      await fetchRail(id);
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Failed to load conversations.';
      askers = [];
    } finally {
      loading = false;
    }
  }

  // The selected member's transcript. One request per member read, instead of every
  // member's messages downloaded to render one.
  let streamToken = 0;
  // community-effect-ok: this is keyed on the selected ASKER as well as the community,
  // and it must also CLEAR the stream when either goes null — neither fits
  // `onCommunityChange`, whose contract is "run once per community". Both store fields
  // are still read unconditionally, which is the part that matters.
  $effect(() => {
    const key = selectedKey;
    const loaded = community.loaded;
    const selected = community.selectedId;
    const id = loaded ? selected : lastId;
    untrack(() => {
      if (key == null || id == null) {
        streamToken += 1;
        convs = [];
        return;
      }
      void loadStream(id, key);
    });
  });

  async function loadStream(id: number, key: string): Promise<void> {
    const token = ++streamToken;
    streamLoading = true;
    try {
      const page = await fetchConversations(id, {
        asker: key,
        withMessages: true,
        limit: RAIL_PAGE,
      });
      if (token !== streamToken) return;
      convs = page.items;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      if (token === streamToken) convs = [];
    } finally {
      if (token === streamToken) streamLoading = false;
    }
  }

  void init();
</script>

<AdminShell {me}>
  {#if loading}
    <StateBlock state="loading" />
  {:else if error}
    <StateBlock state="error" message={error} onretry={init} />
  {:else if askersTotal === 0}
    <StateBlock state="empty" message="No transcripts yet" hint="Member conversations will appear here as they happen." />
  {:else}
    <PageContainer fill class="overflow-hidden">
      <PageHeader section="Sideguide AI" title="Conversations" />

      <!-- THE ONE PAGE WITH NO CONTAINER, and the reason it had none: it is a mail-style
           two-pane view that must fill the viewport, so it measured its own height
           (`h-[calc(100dvh-3.25rem)] lg:h-dvh`). Both numbers were guesses about chrome
           it cannot see — `lg:h-dvh` in particular ignored the shell's community header
           entirely, making the panes taller than the space under it.
           `min-h-0 flex-1` inside the frame's flex column asks for "whatever is left"
           instead, which is the same answer without the arithmetic, and it is what lets
           this page take the same frame as the other nineteen. -->
      <div class="flex min-h-0 flex-1 overflow-hidden rounded-box border hair bg-base-100">
        <!-- Members rail -->
        <aside
          class="w-full shrink-0 flex-col border-r hair bg-base-100 md:flex md:w-80
            {mobileDetail ? 'hidden md:flex' : 'flex'}"
        >
          <div class="flex flex-col gap-2 border-b hair px-4 pt-4 pb-3">
            <div class="flex items-baseline justify-between">
              <span class="eyebrow">Members</span>
              <span class="font-mono text-xs text-base-content/62">
                {#if askersTotal != null && askersTotal > askers.length}{askers.length} of {askersTotal.toLocaleString()}{:else}{askers.length.toLocaleString()}{/if} asking
              </span>
            </div>
            <input
              type="search"
              placeholder="Search members…"
              class="input input-bordered input-sm bg-base-100 font-normal"
              bind:value={q}
              aria-label="Search members"
            />
            {#if sourceFilter}
              <button
                class="flex items-center gap-1.5 self-start rounded-full bg-secondary/10 px-2.5 py-1 text-[0.7rem] font-medium text-secondary transition-colors hover:bg-secondary/20"
                onclick={() => (sourceFilter = null)}
                title="Clear content filter"
              >
                <span class="max-w-[12rem] truncate">Citing: {sourceFilter}</span>
                <span aria-hidden="true">✕</span>
              </button>
            {/if}
          </div>
          {#if filteredUsers.length === 0}
            <StateBlock state="empty" message="No members" hint="Nobody matches that search." compact />
          {:else}
            <ul class="min-h-0 flex-1 space-y-0.5 overflow-y-auto px-2 py-2">
              {#each filteredUsers as u (u.key)}
                {@const on = u.key === selectedKey}
                <li>
                  <button
                    class="flex w-full items-center gap-2.5 rounded-field border-l-[3px] px-3 py-2.5 text-left transition-colors
                      {on
                        ? 'border-primary bg-primary/[0.08] shadow-sm shadow-primary/5'
                        : 'border-transparent hover:bg-base-200/50'}"
                    onclick={() => selectUser(u.key)}
                  >
                    <Avatar src={u.member?.picture_profile ?? null} name={displayName(u)} size={28} memberId={u.member?.member_id} />
                    <span class="min-w-0 flex-1">
                      <span class="redact block truncate text-sm {on ? 'font-semibold text-primary' : 'font-medium text-base-content/85'}">{displayName(u)}</span>
                      <span class="block truncate font-mono text-[0.7rem] text-base-content/75">
                        {#if u.handle}<span class="redact">@{u.handle}</span> · {/if}{u.conversations} conv · {u.questions} q
                      </span>
                    </span>
                  </button>
                </li>
              {/each}
              {#if askersCursor}
                <li class="px-1 py-2">
                  <button class="btn btn-ghost btn-xs w-full font-mono" disabled={moreLoading} onclick={loadMore}>
                    {moreLoading ? 'Loading…' : 'Load more members'}
                  </button>
                </li>
              {/if}
            </ul>
          {/if}
        </aside>

        <!-- Transcript -->
        <main bind:this={scroller} class="flex-1 overflow-y-auto {mobileDetail ? 'block' : 'hidden md:block'}">
          {#if selected}
            {@const who = selected}
            <!-- Full width like every other page: the bubbles carry their own `max-w`,
                 which is what keeps a long message readable — the pane does not need to
                 be narrowed on top of that, and centring it here put the transcript out
                 of line with the rail beside it. -->
            <div class="px-5 pb-6 sm:px-6">
              <button class="btn btn-ghost btn-sm mt-3 -ml-2 md:hidden" onclick={() => (mobileDetail = false)}>← Members</button>
              <header class="sticky top-0 z-10 -mx-5 mb-6 flex items-center gap-3 border-b hair bg-base-100/90 px-5 py-4 backdrop-blur-md sm:-mx-6 sm:px-6">
                {#if who.member}
                  {@const mem = who.member}
                  <MemberHover memberId={mem.member_id} class="shrink-0">
                    <Avatar src={mem.picture_profile} name={displayName(who)} size={44} memberId={mem.member_id} />
                  </MemberHover>
                {:else}
                  <Avatar name={displayName(who)} size={44} />
                {/if}
                <div class="min-w-0 flex-1">
                  <div class="eyebrow mb-0.5">Transcript</div>
                  <h2 class="redact truncate font-display text-2xl font-bold tracking-tight">{displayName(who)}</h2>
                  <div class="mt-0.5 flex flex-wrap items-center gap-x-3 font-mono text-xs text-base-content/75">
                    {#if who.handle}<span class="redact">@{who.handle}</span>{/if}
                    {#if who.external_id}<span class="redact">{who.external_id.slice(0, 12)}</span>{/if}
                    <span>{who.conversations} conv · {who.questions} q</span>
                  </div>
                </div>
                {#if hasAnySources}
                  <button
                    class="btn btn-ghost btn-xs shrink-0 self-start font-mono text-[0.7rem]"
                    onclick={() => (showAllSources = !showAllSources)}
                  >{showAllSources ? 'Hide sources' : 'Expand all sources'}</button>
                {/if}
              </header>

              {#if streamLoading && stream.length === 0}
                <StateBlock state="loading" compact />
              {:else}
                <!-- One chronological chat stream, oldest → newest -->
                <div class="flex flex-col gap-3">
                  {#each stream as it (it.key)}
                    {#if it.kind === 'gap'}
                      <div class="my-1 flex items-center gap-3" role="separator">
                        <div class="h-px flex-1 bg-base-content/10"></div>
                        <span class="shrink-0 rounded-full bg-base-200 px-2.5 py-0.5 font-mono text-[0.65rem] text-base-content/75">{gapLabel(it.hours)}</span>
                        <div class="h-px flex-1 bg-base-content/10"></div>
                      </div>
                    {:else}
                      {@const m = it.m}
                      <div class="flex flex-col gap-1 {m.role === 'user' ? 'items-end' : 'items-start'}">
                        <div class="eyebrow flex items-center gap-2 px-1">
                          <span class="redact">{m.role === 'user' ? displayName(who) : 'Sideguide'}</span>
                          {#if m.created_at}<span class="normal-case tracking-normal opacity-70">{fmt(m.created_at)}</span>{/if}
                        </div>
                        <div
                          class="max-w-[85%] rounded-box px-3.5 py-2.5 text-sm leading-relaxed
                            {m.role === 'user' ? 'border border-primary/25 bg-primary/10' : 'border hair bg-base-100/60'}"
                        ><SkoolContent text={m.content} /></div>
                        {#if m.role === 'assistant' && (m.sources.length > 0 || m.feedback)}
                          {@const fb = m.feedback}
                          <div class="flex max-w-[85%] flex-col gap-1 px-1">
                            <div class="flex flex-wrap items-center gap-2 font-mono text-[0.7rem] text-base-content/75">
                              {#if m.sources.length > 0}
                                <button
                                  class="inline-flex items-center gap-1 transition-colors hover:text-primary"
                                  aria-expanded={isExpanded(m.id)}
                                  onclick={() => toggleSources(m.id)}
                                >
                                  <span class="inline-block w-2 text-center">{isExpanded(m.id) ? '▾' : '▸'}</span>
                                  {m.sources.length} source{m.sources.length === 1 ? '' : 's'}
                                </button>
                              {/if}
                              {#if fb}
                                <span
                                  class="rounded px-1.5 py-px {fb.rating === 'up' ? 'bg-success/15 text-success' : 'bg-error/15 text-error'}"
                                  title={fb.comment ?? ''}
                                >{fb.rating === 'up' ? 'helpful' : 'not helpful'}{#if fb.comment} · “{fb.comment}”{/if}</span>
                              {/if}
                            </div>
                            {#if isExpanded(m.id) && m.sources.length > 0}
                              <ol class="ml-1.5 list-decimal space-y-0.5 pl-4 text-[0.72rem] leading-relaxed text-base-content/72 marker:text-base-content/58">
                                {#each m.sources as s, si (si)}
                                  <li><span class="redact text-base-content/80">{s.page_title ?? 'Untitled'}</span>{#if s.course_title}<span class="text-base-content/62"> · {s.course_title}</span>{/if}</li>
                                {/each}
                              </ol>
                            {/if}
                          </div>
                        {/if}
                      </div>
                    {/if}
                  {/each}
                </div>
              {/if}
            </div>
          {:else}
            <StateBlock state="empty" message="No member selected" hint="Pick a member to read their transcript." />
          {/if}
        </main>
      </div>
    </PageContainer>
  {/if}
</AdminShell>
